"use client"; // Ensure this is a Client Component

import React, { useState, useEffect } from "react";
import { Link } from "@nextui-org/link"; // Use Next.js Link component
import { Input, Textarea, Button, Spacer } from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DefaultLayout from "@/layouts/default";
import { booksAPI, checkAuthentication } from "@/services/api"; // Import the books API service

interface BookFormData {
  name: string;
  author: string;
  category: string;
  language: string;
  description: string;
  pages: string;
  publisher: string;
  publicationDate: string;
  pageType: string;
  pageSize: string;
  stock: number;
  min_stocks: number;
  price: number;
  price_per_book: number;
}

export default function AddBookPage() {
  const { t } = useTranslation(); // Initialize translation hook
  const navigate = useNavigate();

  const [book, setBook] = useState<BookFormData>({
    name: "",
    author: "",
    category: "",
    language: "",
    description: "",
    pages: "",
    publisher: "",
    publicationDate: "",
    pageType: "",
    pageSize: "",
    stock: 0,
    min_stocks: 0,
    price: 0,
    price_per_book: 0,
  });

  // Check authentication on component mount
  useEffect(() => {
    const verifyAuth = async () => {
      const authStatus = await checkAuthentication();

      console.log("Authentication status:", authStatus);

      if (!authStatus.authenticated) {
        alert(t("login_required"));
        navigate("/login");
      }
    };

    verifyAuth();
  }, [navigate, t]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    // Handle numeric fields
    if (["stock", "min_stocks", "price", "price_per_book"].includes(name)) {
      let numValue: number;

      if (name === "stock" || name === "min_stocks") {
        numValue = parseInt(value) || 0;
      } else {
        numValue = parseFloat(value) || 0;
      }

      setBook((prev) => ({ ...prev, [name]: numValue }));
    } else {
      setBook((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      // Debug authentication status
      const token = localStorage.getItem("token");

      console.log("Authentication token present:", !!token);
      if (!token) {
        alert(t("login_required"));
        navigate("/login");

        return;
      }

      // Retrieve client ID from local storage
      const clientId = localStorage.getItem("clientId");

      console.log("Client ID present:", !!clientId);

      if (clientId) {
        // Prepare the book object with all required fields
        const bookData = {
          name: book.name,
          author: book.author,
          category: book.category,
          stock: book.stock,
          price: book.price,
          publisher: book.publisher,
          pricePerBook: book.price_per_book,
          minStocks: book.min_stocks,
          clientId,
          description: book.description,
          language: book.language,
          pages: book.pages,
          publicationDate: book.publicationDate,
          pageType: book.pageType,
          pageSize: book.pageSize,
        };

        console.log("Sending book data:", bookData);

        // Create the book using the API service
        const response = await booksAPI.create(bookData);

        console.log("Book added successfully:", response);

        // Reset form
        setBook({
          name: "",
          author: "",
          category: "",
          language: "",
          description: "",
          pages: "",
          publisher: "",
          publicationDate: "",
          pageType: "",
          pageSize: "",
          stock: 0,
          min_stocks: 0,
          price: 0,
          price_per_book: 0,
        });

        alert(t("book_added_successfully"));
        navigate(`/bookList`);
      } else {
        alert(t("client_id_missing"));
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Backend error:", error.response.data);
      }
      console.error("Error adding book:", error);
      alert(t("failed_to_add_book"));
    }
  };

  return (
    <DefaultLayout>
      <section className="flex flex-col items-center justify-center gap-4 px-4 py-6">
        <div className="inline-block max-w-full lg:min-w-[80vh] text-center">
          <h1 className="text-lg mb-4 font-semibold">{t("add_new_book")}</h1>
          <form
            className="grid grid-cols-2 sm:grid-cols-2 gap-4"
            onSubmit={handleSubmit}
          >
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("name")}
              name="name"
              value={book.name}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("author")}
              name="author"
              value={book.author}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("category")}
              name="category"
              value={book.category}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("language")}
              name="language"
              value={book.language}
              onChange={handleChange}
            />
            <Textarea
              fullWidth
              className="col-span-2 bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("description")}
              name="description"
              value={book.description}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("pages")}
              name="pages"
              type="number"
              value={book.pages}
              onChange={handleChange}
            />
            <Input
              fullWidth
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("publisher")}
              name="publisher"
              value={book.publisher}
              onChange={handleChange}
            />
            <Input
              fullWidth
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("publication_date")}
              name="publicationDate"
              type="date"
              value={book.publicationDate}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("page_type")}
              name="pageType"
              value={book.pageType}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("minimum_stock")}
              name="min_stocks"
              type="number"
              value={book.min_stocks.toString()}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("page_size")}
              name="pageSize"
              value={book.pageSize}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("stock")}
              name="stock"
              type="number"
              value={book.stock.toString()}
              onChange={handleChange}
            />
            <Input
              fullWidth
              required
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("purchase_price")}
              name="price"
              type="number"
              value={book.price.toString()}
              onChange={handleChange}
            />
            <Input
              fullWidth
              className="bg-white dark:bg-gray-800 text-black dark:text-white"
              label={t("price_per_book")}
              name="price_per_book"
              type="number"
              value={book.price_per_book.toString()}
              onChange={handleChange}
            />
            <Button className="col-span-2" color="primary" type="submit">
              {t("add_book")}
            </Button>
          </form>
          <Spacer y={1} />
          <Link className="text-blue-500 mt-4 block" href="/bookList">
            {t("go_to_books_list")}
          </Link>
        </div>
      </section>
    </DefaultLayout>
  );
}
